// Figma 'Portal - Intranet Guide / Button Guide' 기반 신규 버튼 토큰
// 기존 theme/overrides/Button.ts (contained/outlined/text/free) 와 별도 정의.
// 범용 BoxButton/TextButton (@IntranetLibrary/Button) 과
// Accent / AI / Variable variant, BoxIconButton 사이즈가 공유.

type ButtonState = {
  bg: string;
  border: string;
  color: string;
};

export type BoxButtonStateTokens = {
  enabled: ButtonState;
  hover: ButtonState;
  pressed: ButtonState;
  disabled: ButtonState;
};

// ---------- 범용 Box button 4 variant (KeyColor / Primary / Secondary / ETC) ----------
export type BoxButtonVariant = 'keyColor' | 'primary' | 'secondary' | 'caution';

export const BOX_BUTTON_VARIANT_TOKENS: Record<
  BoxButtonVariant,
  BoxButtonStateTokens
> = {
  // 색은 var(--dk-<토큰>, <라이트 값>) — 앱 웹뷰 다크 모드에서만 다크 값 (styles/appDarkMode.ts)
  keyColor: {
    enabled: { bg: 'var(--dk-accent, #06C)', border: 'var(--dk-accent, #06C)', color: '#FFF' },
    hover: { bg: '#3385D6', border: '#3385D6', color: '#FFF' },
    pressed: { bg: '#1A75D1', border: '#1A75D1', color: '#FFF' },
    disabled: { bg: 'var(--dk-accent-tint, #C2DAF3)', border: 'var(--dk-accent-tint, #C2DAF3)', color: 'var(--dk-text-dim, #FFF)' },
  },
  // 검정 버튼 — 다크에서는 앱 네이티브처럼 밝은 버튼으로 뒤집는다 (inverse)
  primary: {
    enabled: { bg: 'var(--dk-inverse-bg, #000)', border: 'var(--dk-inverse-bg, #000)', color: 'var(--dk-inverse-text, #FFF)' },
    hover: { bg: 'var(--dk-inverse-bg-hover, #4D5157)', border: 'var(--dk-inverse-bg-hover, #4D5157)', color: 'var(--dk-inverse-text, #FFF)' },
    pressed: { bg: 'var(--dk-inverse-bg-pressed, #34383D)', border: 'var(--dk-inverse-bg-pressed, #34383D)', color: 'var(--dk-inverse-text, #FFF)' },
    disabled: { bg: 'var(--dk-bg-strong, #D1D8E2)', border: 'var(--dk-bg-strong, #D1D8E2)', color: 'var(--dk-text-dim, #FFF)' },
  },
  secondary: {
    enabled: { bg: 'var(--dk-bg, #FFF)', border: 'var(--dk-line, #E7E9EC)', color: 'var(--dk-text, #000)' },
    hover: { bg: 'var(--dk-bg-subtle, #F9FAFB)', border: 'var(--dk-text-strong, #4D5157)', color: 'var(--dk-text, #000)' },
    pressed: { bg: 'var(--dk-bg-muted, #F3F5F6)', border: 'var(--dk-text-primary, #34383D)', color: 'var(--dk-text, #000)' },
    disabled: { bg: 'var(--dk-bg-subtle, #F9FAFB)', border: 'var(--dk-line, #E7E9EC)', color: 'var(--dk-text-disabled, #D1D8E2)' },
  },
  // 가이드의 ETC — hover/pressed/disabled 는 Secondary 와 동일 명세
  caution: {
    enabled: { bg: 'var(--dk-bg, #FFF)', border: 'var(--dk-line, #E7E9EC)', color: 'var(--dk-danger, #F55060)' },
    hover: { bg: 'var(--dk-bg-subtle, #F9FAFB)', border: 'var(--dk-text-strong, #4D5157)', color: 'var(--dk-danger, #F55060)' },
    pressed: { bg: 'var(--dk-bg-muted, #F3F5F6)', border: 'var(--dk-text-primary, #34383D)', color: 'var(--dk-danger, #F55060)' },
    disabled: { bg: 'var(--dk-bg-subtle, #F9FAFB)', border: 'var(--dk-line, #E7E9EC)', color: 'var(--dk-text-disabled, #D1D8E2)' },
  },
};

// ---------- 범용 Text button tone (Basic / ETC / KeyColor) ----------
// bg 는 tone 공통이라 아래 상수로 분리.
// keyColor 는 가이드 원문에 없던 톤 — 전자결재 QA(PORTAL-13117)에서 #0066CC 확정되며 추가.
export type TextButtonTone = 'default' | 'caution' | 'keyColor';

export const TEXT_BUTTON_TONE_TOKENS: Record<
  TextButtonTone,
  { color: string; hoverColor: string; pressedColor: string }
> = {
  default: { color: '#000', hoverColor: '#4D5157', pressedColor: '#34383D' },
  caution: { color: '#F55060', hoverColor: '#F55060', pressedColor: '#F55060' },
  keyColor: {
    color: '#0066CC',
    hoverColor: '#0066CC',
    pressedColor: '#0066CC',
  },
};

export const TEXT_BUTTON_HOVER_BG = '#F9FAFB';
export const TEXT_BUTTON_PRESSED_BG = '#F3F5F6';
export const TEXT_BUTTON_DISABLED_COLOR = '#D1D8E2';

// ---------- 박스형 Text button (보더 박스 + 배경 상태) ----------
export const BOXED_TEXT_BUTTON_BORDER = '#E7E9EC';
export const BOXED_TEXT_BUTTON_BG = {
  enabled: '#F9FAFB',
  hover: '#F3F5F6',
  pressed: '#E7E9EC',
} as const;

export type TextButtonSize = 'large' | 'medium' | 'small';

export const TEXT_BUTTON_SIZE_SPEC: Record<
  TextButtonSize,
  { fontSize: number; lineHeight: number; iconSize: number }
> = {
  large: { fontSize: 16, lineHeight: 24, iconSize: 24 },
  medium: { fontSize: 14, lineHeight: 20, iconSize: 20 },
  small: { fontSize: 12, lineHeight: 16, iconSize: 16 },
};

// ---------- Accent (Solid Blue) ----------
export const ACCENT_SOLID_TOKENS: BoxButtonStateTokens = {
  enabled: { bg: '#0066CC', border: '#0066CC', color: '#FFFFFF' },
  hover: { bg: '#3385D6', border: '#3385D6', color: '#FFFFFF' },
  pressed: { bg: '#1A75D1', border: '#1A75D1', color: '#FFFFFF' },
  disabled: { bg: '#C2DAF3', border: '#C2DAF3', color: '#FFFFFF' },
};

// ---------- AI Accent (Solid Pink) ----------
export const AI_SOLID_TOKENS: BoxButtonStateTokens = {
  enabled: { bg: '#C77AC4', border: '#C77AC4', color: '#FFFFFF' },
  hover: { bg: '#D295D0', border: '#D295D0', color: '#FFFFFF' },
  pressed: { bg: '#CD87CA', border: '#CD87CA', color: '#FFFFFF' },
  disabled: { bg: '#F2DFF1', border: '#F2DFF1', color: '#FFFFFF' },
};

// ---------- AI Accent (Outline Gradient) ----------
// Figma 디자인은 violet→pink 그라디언트 보더. 단순화하여 violet 단색 사용.
// 추후 디자인시스템에서 그라디언트 보더 토큰 도입 시 교체.
export const AI_OUTLINE_TOKENS: BoxButtonStateTokens = {
  enabled: { bg: '#FFFFFF', border: '#AFB2F4', color: '#C77AC4' },
  hover: { bg: '#F6EAF6', border: '#AFB2F4', color: '#C77AC4' },
  pressed: { bg: '#F8EFF8', border: '#AFB2F4', color: '#C77AC4' },
  disabled: { bg: '#FFFFFF', border: '#E7E9EC', color: '#D1D8E2' },
};

// ---------- Variable Tones (Approval 적용 예시 기반 팔레트) ----------
// "디자인 시스템 내 보유한 팔레트 색상을 박스 버튼에 사용할 때" 의 가이드.
// 임의 색상은 customColor prop 으로 받고, 사전 정의된 톤은 아래 팔레트 사용.
type VariableTonePalette = {
  enabled: string;
  hover: string;
  pressed: string;
};

export const VARIABLE_TONE_PALETTES = {
  success: { enabled: '#43B16F', hover: '#69C18C', pressed: '#56B97D' },
  proceed: { enabled: '#436FB1', hover: '#698CC1', pressed: '#567DB9' },
  caution: { enabled: '#F55060', hover: '#F77380', pressed: '#F66270' },
  rejected: { enabled: '#F55060', hover: '#F77380', pressed: '#F66270' },
  hold: { enabled: '#EFAE00', hover: '#F2BE33', pressed: '#F1B61A' },
} as const satisfies Record<string, VariableTonePalette>;

export type VariableTone = keyof typeof VARIABLE_TONE_PALETTES;

const VARIABLE_DISABLED: ButtonState = {
  bg: '#D1D8E2',
  border: '#D1D8E2',
  color: '#FFFFFF',
};

const VARIABLE_OUTLINE_DISABLED: ButtonState = {
  bg: '#FFFFFF',
  border: '#E7E9EC',
  color: '#D1D8E2',
};

export const buildVariableSolidTokens = (
  tone: VariableTone | VariableTonePalette,
): BoxButtonStateTokens => {
  const p = typeof tone === 'string' ? VARIABLE_TONE_PALETTES[tone] : tone;
  return {
    enabled: { bg: p.enabled, border: p.enabled, color: '#FFFFFF' },
    hover: { bg: p.hover, border: p.hover, color: '#FFFFFF' },
    pressed: { bg: p.pressed, border: p.pressed, color: '#FFFFFF' },
    disabled: VARIABLE_DISABLED,
  };
};

export const buildVariableOutlineTokens = (
  tone: VariableTone | VariableTonePalette,
): BoxButtonStateTokens => {
  const p = typeof tone === 'string' ? VARIABLE_TONE_PALETTES[tone] : tone;
  return {
    enabled: { bg: '#FFFFFF', border: p.enabled, color: p.enabled },
    hover: { bg: '#FFFFFF', border: p.hover, color: p.hover },
    pressed: { bg: '#FFFFFF', border: p.pressed, color: p.pressed },
    disabled: VARIABLE_OUTLINE_DISABLED,
  };
};

// ---------- 사이즈 (라벨 + 좌/우 아이콘 박스 버튼) ----------
export type BoxButtonSize = 'extraLarge' | 'large' | 'medium' | 'small';

export const BOX_BUTTON_SIZE_SPEC: Record<
  BoxButtonSize,
  {
    height: number;
    minWidth: number;
    paddingX: number;
    iconSize: number;
    fontSize: number;
    lineHeight: number;
  }
> = {
  extraLarge: {
    height: 56,
    minWidth: 120,
    paddingX: 16,
    iconSize: 24,
    fontSize: 16,
    lineHeight: 24,
  },
  large: {
    height: 48,
    minWidth: 120,
    paddingX: 12,
    iconSize: 24,
    fontSize: 16,
    lineHeight: 24,
  },
  medium: {
    height: 44,
    minWidth: 80,
    paddingX: 12,
    iconSize: 20,
    fontSize: 14,
    lineHeight: 20,
  },
  small: {
    height: 36,
    minWidth: 64,
    paddingX: 8,
    iconSize: 20,
    fontSize: 14,
    lineHeight: 20,
  },
};

// ---------- 사이즈 (정사각형 아이콘 전용 박스 아이콘 버튼) ----------
export type BoxIconButtonSize = 'xs' | 's' | 'm' | 'l' | 'xl';

export const BOX_ICON_BUTTON_SIZE_SPEC: Record<
  BoxIconButtonSize,
  { side: number; iconSize: number }
> = {
  xs: { side: 28, iconSize: 20 },
  s: { side: 36, iconSize: 20 },
  m: { side: 44, iconSize: 24 },
  l: { side: 48, iconSize: 24 },
  xl: { side: 56, iconSize: 24 },
};

// ---------- 공통 유틸: state token → MUI sx 부분 ----------
/**
 * enabled / hover / focus / pressed / disabled 를 한 벌로 깐다.
 * `BoxButton`(일반 `<button>`) 과 **같은 규칙**이다 — 가이드상 focus 는 hover 와 동일 시각.
 * 상태별로 바뀌는 건 `color` 뿐이므로, 아이콘 svg 가 `stroke`/`fill` 에 `currentColor` 를
 * 쓰면 글자색을 그대로 따라온다.
 */
export const buildBoxButtonStateSx = (tokens: BoxButtonStateTokens) => ({
  transition:
    'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
  backgroundColor: tokens.enabled.bg,
  borderColor: tokens.enabled.border,
  color: tokens.enabled.color,
  // `&:focus` 까지 넣는 건 `BoxButton` 과 맞춘 것이다. 대신 마우스로 누르면 포커스가
  // 남아서 다른 데를 누르기 전까지 hover 색이 유지된다 — 가이드가 focus=hover 라서 그렇다.
  // 키보드 포커스에만 반응시키려면 `&:focus` 를 빼고 `Mui-focusVisible` 만 남기면 된다
  '&:hover, &:focus, &:focus-visible, &.Mui-focusVisible': {
    backgroundColor: tokens.hover.bg,
    borderColor: tokens.hover.border,
    color: tokens.hover.color,
    outline: 'none',
  },
  '&:active': {
    backgroundColor: tokens.pressed.bg,
    borderColor: tokens.pressed.border,
    color: tokens.pressed.color,
  },
  '&.Mui-disabled': {
    backgroundColor: tokens.disabled.bg,
    borderColor: tokens.disabled.border,
    color: tokens.disabled.color,
  },
});
